import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AcceptRequestDialog = ({ appointment, technicians, onClose, onDone }) => {
  const [technicianId, setTechnicianId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    if (!technicianId) return toast.error("Pick a technician");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/appointments/${appointment.id}/accept`, { technician_id: technicianId, note });
      toast.success(data.emailed ? "Accepted – confirmation emailed to the customer" : "Accepted (customer has no email on file)");
      onDone();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Failed to accept"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]" data-testid="accept-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" />Accept booking request</DialogTitle>
          <DialogDescription>{appointment.customer_name} · {appointment.date} at {appointment.time}{appointment.contact_name ? ` · contact ${appointment.contact_name}` : ""}. Assign a technician and we'll email the customer a confirmation (with a reschedule link) and the technician their assignment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Technician</Label>
            <Select value={technicianId} onValueChange={setTechnicianId}>
              <SelectTrigger data-testid="accept-technician-select"><SelectValue placeholder="Choose a technician" /></SelectTrigger>
              <SelectContent>{technicians.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`accept-technician-${t.id}`}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="accept-note">Note to customer (optional)</Label><Textarea id="accept-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please make sure the generator area is accessible." data-testid="accept-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="accept-cancel">Cancel</Button>
          <Button onClick={accept} disabled={busy || !technicianId} className="bg-emerald-600 hover:bg-emerald-700" data-testid="accept-confirm">{busy ? "Accepting…" : "Accept & confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
